import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { X, Search, Loader2, FolderKanban, ChevronDown, MapPin, Hash } from 'lucide-react';
import { toast } from 'sonner';
import { useUIPreview } from '../contexts/UIPreviewContext';
import { useActivityBar } from '../contexts/ActivityBarContext';
import ProjectDetailPanel from './ProjectTracking/ProjectDetailPanel';
import { AddBarbourIdModal } from './AddBarbourIdModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TEAL = '#0d9488';
const fieldCardClass = 'rounded-xl border border-gray-200 bg-white shadow-sm p-3';
const inputClass = 'rounded-lg border-2 border-gray-300 bg-white focus:border-teal-400 focus:ring-2 focus:ring-teal-200 h-10';

/**
 * "Project" activity — the Project counterpart to ContactNotesTasksModal
 * (Customer Management). Search finds projects directly (name / address /
 * postcode / Barbour ID) OR via a matching customer (shows all of that
 * customer's projects), then renders the exact same ProjectDetailPanel used
 * on the full Project Tracking page, inside a modal. Opts into the Activity
 * Bar (Open/Recent tabs) the same way Customer Management does.
 */
export const ProjectManagementModal = ({ isOpen, onClose, initialProjectId = null, initialSearch = null, user, resumeSnapshot = null }) => {
  const { modalUiV2 } = useUIPreview();
  const activityBar = useActivityBar();

  const [view, setView] = useState('search'); // 'search' | 'detail'
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loadingProject, setLoadingProject] = useState(false);

  // "Browse Lists" — same tracking-list folders as the full Project Tracking page
  const [showLists, setShowLists] = useState(false);
  const [lists, setLists] = useState([]);
  const [listsLoading, setListsLoading] = useState(false);
  const [activeListId, setActiveListId] = useState(null);
  const [listProjectNames, setListProjectNames] = useState({});
  const [addBarbourFor, setAddBarbourFor] = useState(null); // { id, name } — project missing a Barbour ID

  const resetAll = () => {
    setView('search'); setQuery(''); setResults([]); setSelectedProject(null);
    setShowLists(false); setActiveListId(null);
  };

  const openProject = async (id, fallbackTitle, opts = {}) => {
    setLoadingProject(true);
    setView('detail');
    try {
      const res = await api.get(`${API}/zoho/project/${id}`);
      const project = res.data?.project;
      setSelectedProject(project);
      // Resuming an already-active/host-rendered activity (Open/Recent tab
      // reopen) must NOT re-register — doing so would flip the Activity Bar's
      // `renderedByHost` flag back to false, causing ActivityModalHost to
      // unmount the modal it just rendered (looked like an instant open+close).
      if (modalUiV2 && project && !opts.isResume) {
        const snapshot = { selectedProjectId: project.id };
        activityBar.openActivity({ type: 'project_management', activityId: project.id, title: project.name || fallbackTitle || 'Project', subtitle: 'Project', snapshot });
        activityBar.logRecentView({ type: 'project_management', activityId: project.id, title: project.name || fallbackTitle || 'Project', subtitle: 'Project', snapshot });
      }
    } catch (_) {
      toast.error('Failed to load project');
      setView('search');
    } finally {
      setLoadingProject(false);
    }
  };

  const runSearch = async (q) => {
    if (!q?.trim() || q.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const [projRes, acctRes] = await Promise.allSettled([
        api.get(`${API}/zoho/search-projects`, { params: { q: q.trim() } }),
        api.get(`${API}/zoho/search-accounts`, { params: { q: q.trim() } }),
      ]);
      const direct = (projRes.status === 'fulfilled' ? projRes.value.data?.projects : []) || [];
      const seen = new Set(direct.map(p => p.id));
      const merged = direct.map(p => ({
        id: p.id, name: p.deal_name || p.barbour_project_name, account_name: p.account_name,
        postcode: p.postcode, town: p.town, barbour_project_id: p.barbour_project_id,
      }));

      const accounts = (acctRes.status === 'fulfilled' ? acctRes.value.data?.accounts : []) || [];
      const topAccounts = accounts.slice(0, 3);
      if (topAccounts.length > 0) {
        const qpResults = await Promise.all(topAccounts.map(a =>
          api.get(`${API}/zoho/account/${a.id}/quotes-and-projects`)
            .then(r => ({ account: a, projects: r.data?.projects || [] }))
            .catch(() => ({ account: a, projects: [] }))
        ));
        qpResults.forEach(({ account, projects }) => {
          projects.forEach(p => {
            if (!seen.has(p.id)) {
              seen.add(p.id);
              merged.push({ id: p.id, name: p.name, account_name: account.name, _fromAccount: account.name });
            }
          });
        });
      }
      setResults(merged);
    } catch (_) {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (resumeSnapshot?.selectedProjectId) {
        openProject(resumeSnapshot.selectedProjectId, null, { isResume: true });
      } else if (initialProjectId) {
        openProject(initialProjectId);
      } else if (initialSearch) {
        resetAll();
        setTimeout(() => { setQuery(initialSearch); runSearch(initialSearch); }, 0);
      } else {
        resetAll();
      }
    }
  }, [isOpen]); // eslint-disable-line

  useEffect(() => {
    if (!isOpen || view !== 'search') return;
    if (!query.trim()) { setResults([]); return; }
    const t = setTimeout(() => runSearch(query), 300);
    return () => clearTimeout(t);
  }, [query, isOpen, view]);

  const loadLists = async () => {
    if (lists.length > 0) { setShowLists(v => !v); return; }
    setListsLoading(true);
    try {
      const res = await api.get(`${API}/project-tracking/lists`);
      setLists(res.data?.lists || []);
      setShowLists(true);
    } catch (_) {
      toast.error('Failed to load tracking lists');
    } finally {
      setListsLoading(false);
    }
  };

  const selectList = async (list) => {
    setActiveListId(list.id);
    const ids = (list.project_ids || []).slice(0, 30);
    const unfetched = ids.filter(id => !listProjectNames[id]);
    if (unfetched.length) {
      const fetched = await Promise.allSettled(
        unfetched.map(id => api.get(`${API}/zoho/project/${id}`).then(r => ({ id, name: r.data?.project?.name || id })))
      );
      const updates = {};
      fetched.forEach(r => { if (r.status === 'fulfilled') updates[r.value.id] = r.value.name; });
      setListProjectNames(prev => ({ ...prev, ...updates }));
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className={`fixed left-0 right-0 bottom-0 flex items-start justify-center ${modalUiV2 ? 'p-3' : 'p-3 pt-8'} ${modalUiV2 ? 'bg-black/50' : 'bg-black/70 backdrop-blur-sm'}`}
      style={modalUiV2 ? { top: activityBar.headerHeight, zIndex: 45 } : { top: 0, zIndex: 80 }}
      data-testid="pmm-modal-overlay"
    >
      <div
        className="bg-gradient-to-br from-teal-50/40 via-white to-teal-50/20 rounded-3xl shadow-2xl w-full max-w-[1000px] max-h-[95vh] overflow-y-auto border-2 border-teal-300 flex flex-col [overscroll-behavior:contain]"
        style={modalUiV2 ? { maxHeight: `calc(100vh - ${activityBar.headerHeight}px - 24px)` } : undefined}
        data-testid="pmm-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-teal-100 bg-white/60 rounded-t-3xl flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0" style={{ background: 'linear-gradient(135deg, #0d9488 0%, #2dd4bf 100%)' }}>
              <FolderKanban className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-extrabold text-gray-900 tracking-tight leading-tight">Project</h2>
              <p className="text-[11px] text-gray-500 truncate">
                {selectedProject ? selectedProject.name : 'Search a project or customer to get started'}
              </p>
            </div>
          </div>
          <button
            onClick={() => { if (modalUiV2) activityBar.discardActive(); resetAll(); onClose?.(); }}
            className="text-gray-400 hover:text-gray-700 rounded-full p-1.5 hover:bg-gray-100 flex-shrink-0"
            data-testid="pmm-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          {/* ══ VIEW: SEARCH ══ */}
          {view === 'search' && (
            <div className={fieldCardClass}>
              <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Search Project or Customer</Label>
              <div className="flex gap-2 mt-1.5">
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Project name, address, postcode, Barbour ID or customer…"
                  className={`flex-1 ${inputClass}`}
                  autoFocus
                  data-testid="pmm-search-input"
                />
                <Button type="button" onClick={() => runSearch(query)} disabled={searching} className="rounded-xl h-10 w-10 p-0" style={{ backgroundColor: TEAL }}>
                  {searching ? <Loader2 className="w-4 h-4 animate-spin text-white" /> : <Search className="w-4 h-4 text-white" />}
                </Button>
              </div>

              {(searching || results.length > 0) && (
                <div className="border border-gray-200 rounded-xl divide-y max-h-80 overflow-y-auto mt-2 bg-white shadow-inner">
                  {searching ? (
                    <div className="p-3 text-center text-gray-500 text-sm">Searching…</div>
                  ) : results.map((p) => (
                    <div key={p.id} className="w-full flex items-center gap-2 p-3 hover:bg-teal-50 transition-colors">
                      {!p.barbour_project_id && <div className="w-[118px] shrink-0" aria-hidden="true" />}
                      <button
                        type="button"
                        onClick={() => openProject(p.id, p.name)}
                        className="flex-1 min-w-0 text-center"
                        data-testid={`pmm-project-option-${p.id}`}
                      >
                        <div className="font-semibold text-sm text-gray-900">{p.name || 'Unnamed Project'}</div>
                        <div className="text-xs text-gray-500 flex items-center justify-center gap-2 flex-wrap mt-0.5">
                          {p.account_name && <span>{p.account_name}</span>}
                          {p.town && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{[p.town, p.postcode].filter(Boolean).join(', ')}</span>}
                          {p.barbour_project_id && <span className="flex items-center gap-0.5"><Hash className="w-3 h-3" />{p.barbour_project_id}</span>}
                          {p._fromAccount && <span style={{ color: TEAL }}>via {p._fromAccount}</span>}
                        </div>
                      </button>
                      {!p.barbour_project_id && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setAddBarbourFor({ id: p.id, name: p.name }); }}
                          className="w-[118px] shrink-0 flex items-center justify-center gap-1 text-[11px] font-bold px-2.5 py-1.5 rounded-lg transition-opacity hover:opacity-90 whitespace-nowrap text-white"
                          style={{ background: TEAL }}
                          data-testid={`pmm-add-barbour-id-${p.id}`}
                        >
                          <Hash className="w-3 h-3" /> Add Barbour ID
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {!searching && results.length === 0 && query.trim().length >= 2 && (
                <div className="text-center text-xs text-gray-400 py-3">No matching projects or customers found</div>
              )}
            </div>
          )}

          {/* ══ VIEW: PROJECT DETAIL ══ */}
          {view === 'detail' && (
            <>
              <div className="flex items-center gap-2 rounded-xl bg-teal-50 border border-teal-200 px-4 py-2.5 relative">
                <FolderKanban className="w-4 h-4 text-teal-600 flex-shrink-0" />
                <span className="font-bold text-gray-900 flex-1 truncate text-sm">{selectedProject?.name || 'Loading…'}</span>
                <button
                  onClick={loadLists}
                  className="text-xs text-teal-700 hover:text-teal-900 font-semibold flex items-center gap-1 shrink-0"
                  data-testid="pmm-browse-lists-btn"
                >
                  {listsLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <FolderKanban className="w-3 h-3" />}
                  Browse Lists <ChevronDown className={`w-3 h-3 transition-transform ${showLists ? 'rotate-180' : ''}`} />
                </button>
                <button
                  onClick={() => { setView('search'); setSelectedProject(null); setQuery(''); setResults([]); setShowLists(false); }}
                  className="text-xs text-gray-400 hover:text-gray-600 shrink-0 underline"
                  data-testid="pmm-change-project"
                >
                  Change
                </button>

                {showLists && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-xl border border-gray-200 z-50 max-h-72 overflow-y-auto">
                    {lists.length === 0 && <div className="p-3 text-xs text-gray-400 text-center">No tracking lists found</div>}
                    {lists.map(list => (
                      <div key={list.id}>
                        <button
                          onClick={() => selectList(list)}
                          className={`w-full flex items-center justify-between px-3 py-2 text-left text-sm font-medium hover:bg-teal-50 ${activeListId === list.id ? 'bg-teal-50' : ''}`}
                          data-testid={`pmm-list-option-${list.id}`}
                        >
                          <span className="truncate">{list.name}</span>
                          <span className="text-[11px] text-gray-400">{(list.project_ids || []).length}</span>
                        </button>
                        {activeListId === list.id && (
                          <div className="pl-4 pb-1 space-y-0.5">
                            {(list.project_ids || []).map(pid => (
                              <button
                                key={pid}
                                onClick={() => { openProject(pid, listProjectNames[pid]); setShowLists(false); }}
                                className="w-full text-left text-xs text-gray-600 hover:text-teal-700 py-1 truncate"
                                data-testid={`pmm-list-project-${pid}`}
                              >
                                {listProjectNames[pid] || pid}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {showLists && <div className="fixed inset-0 z-40" onClick={() => setShowLists(false)} />}
              </div>

              {loadingProject && (
                <div className="p-8 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
              )}
              {!loadingProject && selectedProject && (
                <ProjectDetailPanel project={selectedProject} user={user} />
              )}
            </>
          )}
        </div>
      </div>

      <AddBarbourIdModal
        isOpen={!!addBarbourFor}
        onClose={() => setAddBarbourFor(null)}
        projectId={addBarbourFor?.id}
        projectName={addBarbourFor?.name}
        onAdded={() => { setAddBarbourFor(null); if (query) runSearch(query); }}
      />
    </div>,
    document.body
  );
};

export default ProjectManagementModal;
